import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ShieldCheck, Loader2 } from "lucide-react";
import logger from "@/lib/logger";

/**
 * Printable one-page "Regulatory Snapshot" for a device.
 * URL: /onboarding/snapshot/:projectId?ecrf=...
 * Opens in a new tab from the Onboarding wizard's final step.
 * User hits Cmd/Ctrl+P (or the on-page button) → browser's Save-as-PDF.
 */
export default function OnboardingSnapshot() {
  const { projectId } = useParams();
  const [search] = useSearchParams();
  const ecrfB64 = search.get("ecrf");
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  let ecrf = null;
  if (ecrfB64) {
    try {
      ecrf = JSON.parse(decodeURIComponent(escape(atob(ecrfB64))));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        logger.warn("snapshot ecrf decode failed:", e?.message);
      }
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/devices/${projectId}`);
        setDevice(data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          setError("This device project was not found. It may have been deleted or you may not have access.");
        } else if (status === 401 || status === 403) {
          setError("You are not authorized to view this snapshot. Please sign in and try again.");
        } else {
          setError("We couldn't load the snapshot right now. Please refresh, or return to the dashboard and try again.");
        }
        if (process.env.NODE_ENV !== "production") {
          logger.warn("snapshot device fetch failed:", status, e?.message);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error || !device) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="snapshot-error-state">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center shadow-sm">
          <div className="text-slate-900 font-semibold mb-2">Snapshot unavailable</div>
          <div className="text-sm text-slate-600 mb-4">{error || "Device not found."}</div>
          <Button
            onClick={() => window.close()}
            data-testid="snapshot-close-btn"
            variant="outline"
            className="text-slate-700"
          >
            Close tab
          </Button>
        </div>
      </div>
    );
  }

  const c = device.classification || {};
  const fda = c.fda || {}; const eu = c.eu_mdr || {}; const cdsco = c.cdsco || {};
  const now = new Date().toLocaleDateString();

  return (
    <div className="bg-slate-100 min-h-screen py-8 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto px-4 print:px-0">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <div className="text-xs text-slate-500 font-mono">Regulatory Snapshot · {device.device_name}</div>
          <Button onClick={() => window.print()} data-testid="snapshot-print-btn"
                  className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Printer className="w-4 h-4 mr-2" /> Save as PDF / Print
          </Button>
        </div>

        <div id="snapshot-doc" className="bg-white shadow-lg p-10 print:shadow-none print:p-0 mx-auto"
             style={{ width: "210mm", minHeight: "297mm" }}>
          <div className="flex items-start justify-between pb-4 border-b-2 border-slate-900 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-6 h-6 text-[#0055FF]" />
                <span className="font-display font-extrabold text-2xl text-slate-900">VitaQMS</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Regulatory Snapshot</div>
              <div className="text-[10px] font-mono text-slate-400">Generated {now}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Safety by design</div>
              <div className="text-[10px] font-mono text-slate-400">Compliance is cheapest when it&apos;s earliest.</div>
            </div>
          </div>

          <h1 className="font-display font-extrabold text-3xl text-slate-900 leading-tight mb-1">{device.device_name}</h1>
          <div className="text-sm text-slate-600 mb-4">{device.device_type}</div>
          <div className="border-l-4 border-l-[#0055FF] bg-slate-50 p-3 mb-6">
            <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">Intended use</div>
            <p className="text-sm text-slate-800">{device.intended_use || "—"}</p>
          </div>

          <div className="mb-6">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Target markets</div>
            <div className="flex flex-wrap gap-2">
              {(device.markets || []).map((m) => (
                <span key={m} className="text-xs border border-slate-300 rounded px-2 py-1">{m}</span>
              ))}
              {device.samd && <span className="text-xs border border-[#0055FF] text-[#0055FF] rounded px-2 py-1">SaMD / AI-ML</span>}
            </div>
          </div>

          <h2 className="font-display font-bold text-lg text-slate-900 mb-3 border-b border-slate-200 pb-1">Regulatory classification</h2>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <ClassBlock label="FDA (US)" cls={fda} />
            <ClassBlock label="EU MDR" cls={eu} />
            <ClassBlock label="CDSCO (India)" cls={cdsco} />
          </div>

          {c.rule_verification?.requires_human_review && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 mb-6">
              <b>Human review flagged:</b> the symbolic rule verifier disagreed with the AI verdict on at least one market.
              A qualified regulatory reviewer must reconcile before submission.
            </div>
          )}

          <h2 className="font-display font-bold text-lg text-slate-900 mb-3 border-b border-slate-200 pb-1">Quality workflows opening for you</h2>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-700 mb-6">
            <div>• CAPA + Deviations</div>
            <div>• Risk Management (ISO 14971)</div>
            <div>• Change Control (21 CFR Part 11 e-sigs)</div>
            <div>• Clinical eCRF (7 principles)</div>
            <div>• Complaints / MDR / MIR / MvPI</div>
            <div>• Live regulatory alerts + impact analysis</div>
            <div>• Design History File (DHF)</div>
            <div>• Inspector portal + audit trails</div>
          </div>

          {ecrf && (
            <>
              <h2 className="font-display font-bold text-lg text-slate-900 mb-3 border-b border-slate-200 pb-1">
                First clinical eCRF template (AI-drafted)
              </h2>
              <div className="text-sm text-slate-800 mb-1"><b>{ecrf.name || "Study visit form"}</b></div>
              <div className="text-xs text-slate-500 mb-3">
                {(ecrf.fields || []).length} field(s) · {(ecrf.skip_rules || []).length} skip rule(s) ·
                {" "}{(ecrf.endpoints_suggested || []).length} endpoint(s) suggested
              </div>
              <ul className="text-xs text-slate-700 space-y-1 mb-6 pl-4 list-disc">
                {(ecrf.fields || []).slice(0, 8).map((f, i) => (
                  <li key={`ecrf-f-${i}`}>
                    <b>{f.label}</b> — {f.type}
                    {f.mandatory ? " · mandatory" : ""}
                    {f.constraints?.unit ? ` (${f.constraints.unit})` : ""}
                  </li>
                ))}
                {(ecrf.fields || []).length > 8 && <li className="italic text-slate-500">+ {(ecrf.fields || []).length - 8} more…</li>}
              </ul>
            </>
          )}

          <div className="mt-8 pt-4 border-t border-slate-200 text-[10px] font-mono text-slate-500 leading-relaxed">
            <b>Disclaimer.</b> This snapshot is AI-assisted and informational only. All classifications, workflows, and templates must
            be reviewed, validated, and approved by qualified personnel under the customer&apos;s own quality management processes
            before use in a regulated submission. Nothing here constitutes legal, regulatory, or medical advice.
          </div>
        </div>
      </div>
    </div>
  );
}

function ClassBlock({ label, cls }) {
  return (
    <div className="border border-slate-300 rounded p-3">
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display font-bold text-2xl text-slate-900 mt-1">{cls.class || "—"}</div>
      {cls.rationale && (
        <p className="text-[10px] text-slate-600 mt-1 leading-tight">{cls.rationale.slice(0, 180)}{cls.rationale.length > 180 ? "…" : ""}</p>
      )}
    </div>
  );
}
